import { useState } from "react";
import { MessageCircle, Send, FileText, CheckCircle2 } from "lucide-react";
import { WHATSAPP_URL } from "@/lib/constants";

const FEATURES = [
  "Análise técnica do desenho enviado",
  "Orçamento consolidado por lote ou unidade",
  "Prazo estimado de produção e logística",
];

export default function CTASection() {
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 4000);
  };

  return (
    <section
      id="cta"
      data-testid="cta-section"
      className="relative py-20 sm:py-28 overflow-hidden"
      style={{ background: "var(--buzas-navy)" }}
    >
      {/* Soft gradient accent */}
      <div
        aria-hidden
        className="absolute -top-40 -left-40 w-[520px] h-[520px] rounded-full opacity-20"
        style={{
          background:
            "radial-gradient(closest-side, #3a5aa0, transparent)",
        }}
      />

      <div className="container-buzas relative">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          {/* LEFT */}
          <div className="lg:col-span-6">
            <span
              className="eyebrow"
              style={{ color: "#ff8a8a" }}
            >
              Fale com a Buzas
            </span>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl leading-[1.08] tracking-tight text-white">
              Solicite um orçamento técnico agora.
              <br />
              <span className="text-white/85">
                Envie seu desenho para análise.
              </span>
            </h2>
            <p className="mt-5 text-base sm:text-lg text-white/80 leading-relaxed max-w-xl">
              Retornamos com orientação técnica e prazo estimado.
            </p>

            <div className="mt-8 flex flex-wrap gap-4">
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noreferrer"
                data-testid="cta-whatsapp-btn"
                className="btn-primary"
              >
                <MessageCircle size={18} />
                Falar no WhatsApp
              </a>
              <a
                href="#orcamento-form"
                data-testid="cta-quote-btn"
                className="btn-outline-light"
              >
                Solicitar orçamento
              </a>
            </div>

            <ul className="mt-10 space-y-3">
              {FEATURES.map((f, i) => (
                <li
                  key={f}
                  data-testid={`cta-feature-${i}`}
                  className="flex items-center gap-3 text-[15px] text-white/90 font-semibold"
                >
                  <CheckCircle2
                    size={18}
                    className="text-[var(--buzas-red)] shrink-0"
                  />
                  {f}
                </li>
              ))}
            </ul>
          </div>

          {/* RIGHT — form card */}
          <div className="lg:col-span-6">
            <form
              id="orcamento-form"
              onSubmit={handleSubmit}
              data-testid="quote-form"
              className="bg-white rounded-xl p-8 sm:p-10 shadow-[0_25px_60px_-20px_rgba(0,0,0,0.35)]"
            >
              <div className="flex items-center gap-3 mb-2">
                <FileText size={16} className="text-[var(--buzas-red)]" />
                <span className="eyebrow">Solicitar orçamento</span>
              </div>
              <h3 className="font-display text-[22px] sm:text-2xl font-bold text-[var(--buzas-navy)] leading-tight">
                Receba análise técnica personalizada
              </h3>

              <div className="mt-7 space-y-5">
                <Field label="Nome completo">
                  <input
                    type="text"
                    required
                    placeholder="Seu nome"
                    data-testid="form-input-name"
                    className="form-input"
                  />
                </Field>

                <div className="grid sm:grid-cols-2 gap-5">
                  <Field label="E-mail">
                    <input
                      type="email"
                      required
                      placeholder="voce@empresa.com.br"
                      data-testid="form-input-email"
                      className="form-input"
                    />
                  </Field>
                  <Field label="Telefone / WhatsApp">
                    <input
                      type="tel"
                      required
                      placeholder="(00) 00000-0000"
                      data-testid="form-input-phone"
                      className="form-input"
                    />
                  </Field>
                </div>

                <Field label="Descreva a peça / projeto">
                  <textarea
                    required
                    rows={4}
                    placeholder="Quantidade, material, prazo desejado, aplicação na aeronave..."
                    data-testid="form-input-message"
                    className="form-input resize-none"
                  />
                </Field>
              </div>

              <button
                type="submit"
                data-testid="form-submit-btn"
                className="btn-primary w-full mt-7"
              >
                <Send size={16} />
                Solicitar orçamento
              </button>

              {submitted && (
                <p
                  data-testid="form-feedback"
                  className="mt-4 text-sm text-[var(--buzas-navy)] bg-[rgba(37,211,102,0.1)] border border-[var(--wa-green)]/30 rounded-md p-3"
                >
                  Recebemos sua solicitação. Em breve retornaremos com análise
                  técnica.
                </p>
              )}

              <p className="mt-5 text-xs text-[var(--buzas-muted)] leading-relaxed">
                Ao enviar, você concorda em ser contatado pela equipe Buzas
                para orientação técnica e prazo estimado.
              </p>
            </form>
          </div>
        </div>
      </div>

      <style>{`
        .form-input {
          width: 100%;
          background: #ffffff;
          border: 1px solid var(--buzas-border);
          border-radius: 6px;
          padding: 12px 14px;
          font-size: 15px;
          color: var(--buzas-navy);
          outline: none;
          transition: border-color 0.2s ease, box-shadow 0.2s ease;
          font-family: "Titillium Web", sans-serif;
        }
        .form-input::placeholder {
          color: #97a1b6;
        }
        .form-input:focus {
          border-color: var(--buzas-red);
          box-shadow: 0 0 0 3px rgba(217, 54, 54, 0.12);
        }
      `}</style>
    </section>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[12px] font-bold uppercase tracking-[0.14em] text-[var(--buzas-navy)] mb-2">
        {label}
      </span>
      {children}
    </label>
  );
}
